import React, { useState } from 'react';
import '../styles/CancellationModal.css';

// Types
interface ProductTier {
  id: string;
  name: string;
  description: string;
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
  metadata: Record<string, string>;
}

interface UserSubscription {
  subscription_id: string;
  stripe_product_id: string;
  stripe_price_id: string;
  status: string;
  current_period_end: string;
}

interface CancellationModalProps {
  currentTier: ProductTier | null;
  currentSubscription: UserSubscription;
  onConfirm: () => void;
  onCancel: () => void;
  onError: (error: string) => void;
}

const CancellationModal: React.FC<CancellationModalProps> = ({
  currentTier,
  currentSubscription,
  onConfirm,
  onCancel,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [atPeriodEnd, setAtPeriodEnd] = useState(true);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/api/cancel-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          at_period_end: atPeriodEnd,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      onConfirm();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel subscription';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const currentPeriodEnd = new Date(currentSubscription.current_period_end);

  return (
    <div className="cancellation-modal">
      <div className="cancellation-content">
        <div className="cancellation-header">
          <h3>Cancel Subscription</h3>
          <button 
            type="button" 
            className="btn-close" 
            aria-label="Close"
            onClick={onCancel}
          />
        </div>

        <div className="cancellation-summary">
          <div className="current-plan-info">
            <h4>Current Plan: {currentTier?.name || 'Unknown'}</h4>
            <p className="plan-price">
              {currentTier ? formatPrice(currentTier.amount, currentTier.currency) : 'N/A'}
              /{currentTier?.interval || 'month'}
            </p>
            <p className="next-billing">
              Next billing date: {currentPeriodEnd.toLocaleDateString()}
            </p>
          </div>

          <div className="cancellation-options">
            <h5>When would you like to cancel?</h5>
            
            <div className="option">
              <label className="option-label">
                <input
                  type="radio"
                  name="cancellation-timing"
                  value="period-end"
                  checked={atPeriodEnd}
                  onChange={() => setAtPeriodEnd(true)}
                />
                <div className="option-content">
                  <strong>At the end of current billing period</strong>
                  <p>
                    You'll keep access to all features until {currentPeriodEnd.toLocaleDateString()}, 
                    and won't be charged again.
                  </p>
                </div>
              </label>
            </div>

            <div className="option">
              <label className="option-label">
                <input
                  type="radio"
                  name="cancellation-timing"
                  value="immediate"
                  checked={!atPeriodEnd}
                  onChange={() => setAtPeriodEnd(false)}
                />
                <div className="option-content">
                  <strong>Immediately</strong>
                  <p>
                    You'll lose access to all features right away. 
                    No refund will be provided for the current billing period.
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="cancellation-impact">
            <div className="alert alert-warning">
              <h6>What happens when you cancel:</h6>
              <ul>
                <li>You'll lose access to all premium features</li>
                <li>Your account will remain active but with limited functionality</li>
                <li>You can resubscribe at any time to restore full access</li>
                <li>Your data will be preserved for 90 days</li>
              </ul>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="cancellation-actions">
          <button 
            type="button" 
            className="btn btn-secondary me-2" 
            onClick={onCancel}
            disabled={isLoading}
          >
            Keep Subscription
          </button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Canceling...
              </>
            ) : (
              'Confirm Cancellation'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancellationModal;
